import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WarringtonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Warrington Pothole Claims | 22% of Estate Roads RED | Fixtyer"
        description="Warrington Borough Council filled nearly 19,000 potholes in five years and spends 49% above its DfT allocation — yet earns AMBER overall because best practice lags behind GREEN condition and spend. See what this means for your claim."
        canonical="/councils/warrington"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Warrington', url: '/councils/warrington' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Warrington has GREEN condition and spend scorecards — why is the overall rating AMBER?',
            answer: 'The Department for Transport rates Warrington GREEN on both road condition and maintenance spend, but AMBER on best practice — which pulls the overall rating to AMBER. For your claim, condition and whether the specific defect was reasonably inspected matter more than the aggregate spend scorecard. Warrington\'s own data still shows U-roads hitting 22% RED in 2022 and 4,373 pothole fills in 2024/25.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in Warrington?',
            answer: 'U-roads make up 672km — roughly 73% of Warrington\'s 924km carriageway network. The council surveys approximately 50% of U-roads each year using coarse visual inspection (CVI), achieving full coverage every two years. At the 2022 survey, 22% of U-roads were in RED condition. The council itself notes many estate roads were built together during New Town development and are approaching the end of their design life at the same time.',
          },
          {
            question: 'Does Warrington spending 49% above its DfT allocation weaken my claim?',
            answer: 'Not necessarily — it strengthens the council\'s Section 58 defence on aggregate spend. But Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Warrington projected £10.684m capital spend against a £7.184m DfT allocation in 2025/26, yet still filled 4,373 potholes in 2024/25. High reactive volume suggests defects continue forming between inspections.',
          },
          {
            question: 'Does the New Town estate roads admission help my claim?',
            answer: 'Yes. Warrington publicly states that significant sections of its carriageway network were built around the same time as part of New Town development, meaning deterioration rates are largely the same and most roads are approaching the end of their original design life together. That is documented knowledge of elevated risk across large parts of the 672km U-road network — relevant when arguing what a reasonable inspection regime looks like on affected estate roads.',
          },
          {
            question: 'Pothole fills rose to 4,373 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. 2024/25 was the highest pothole-fill year in Warrington\'s five-year published record, up from 4,039 in 2020/21. The council allocated £686,806 — 11.1% of its 2025/26 Highways Maintenance Block — specifically to reactive pothole maintenance. Filling individual defects is reactive work; the underlying U-road condition data and the council\'s own New Town ageing admission show structural pressure remains.',
          },
          {
            question: 'Does motorway diversion traffic affect pothole claims in Warrington?',
            answer: 'It can strengthen context arguments. Warrington sits between the M6, M56 and M62, and the council states that during motorway incidents or closures, high volumes of diverted traffic adversely affect the local highway network. If your defect was on a known diversion route, the council\'s own Resilient Highway Network documentation acknowledges accelerated wear — which raises the bar for what reasonable inspection looks like on those corridors.',
          },
          {
            question: 'Do I need to report the pothole before claiming against Warrington?',
            answer: 'Warrington\'s claims guidance states you should report the defect before submitting a compensation claim and will need the reference number from your report. The council only action potholes at least 4cm deep on roads (2.5cm on footways) and wider than 30cm. Reporting creates a record of notice — valuable evidence even if the council\'s repair thresholds mean smaller defects are not fixed.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Warrington Borough Council"
        councilSlug="warrington"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/warrington"
        headline="Warrington Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Warrington</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Warrington: 22% of estate roads RED at survey peak
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Warrington earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards for both road condition and
            maintenance spend — yet the overall rating is <strong className="text-amber-400">AMBER</strong> because best
            practice lags behind. Meanwhile the council&apos;s own surveys recorded{' '}
            <strong className="text-rose-400">22% of unclassified roads in RED condition in 2022</strong>, nearly{' '}
            <strong className="text-rose-400">19,000 pothole fills in five years</strong>, and an admission that New Town
            estate roads built in the same era are reaching end of design life together across{' '}
            <strong className="text-white">672km of residential network</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">672km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 73% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              Warrington&apos;s 924km carriageway network includes just 252km of A, B and C roads. The remaining 672km of
              U-roads are surveyed by coarse visual inspection on roughly half the network each year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Warrington Borough Council&apos;s June 2025 transparency report —
            classified roads stable, U-roads volatile
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (137.5km — 15% of network): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.0%</td>
                    <td className="px-4 py-2 text-center">15.8%</td>
                    <td className="px-4 py-2 text-center">82.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.4%</td>
                    <td className="px-4 py-2 text-center">14.4%</td>
                    <td className="px-4 py-2 text-center">84.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">14.9%</td>
                    <td className="px-4 py-2 text-center">83.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.3%</td>
                    <td className="px-4 py-2 text-center">14.7%</td>
                    <td className="px-4 py-2 text-center">84.0%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">1.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">16.4%</td>
                    <td className="px-4 py-2 text-center font-bold">82.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition has stayed below 2% throughout the five-year period. Amber rose to 16.4% in 2024 — the
              highest in the series — but main roads remain overwhelmingly green. A-roads are a small fraction of where most
              Warrington motorists live and drive day to day.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (114.5km — 12% of network): consistently strong</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">84%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">86%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">83%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">1%</td>
                    <td className="px-4 py-2 text-center font-bold">16%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">83%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 text-sm">
              B and C roads have held at just 1% RED for four consecutive years. This is the data behind Warrington&apos;s
              GREEN condition scorecard — but it covers only 114.5km of a 924km network.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend — AMBER overall</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.18m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£10.68m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">85%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Warrington projects spending{' '}
              <span className="text-emerald-400">49% above its DfT allocation</span> with 85% classed as preventative
              maintenance — enough for a GREEN spend scorecard. Yet the overall DfT rating stays{' '}
              <span className="text-amber-400">AMBER</span> because best practice does not yet meet the full criteria.
              For motorists, that split matters: the council can point to investment and condition data on classified
              roads, while claims on the 672km U-road network turn on specific inspection and repair records.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road story */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 672km U-road network
          </h2>
          <p className="text-surface-600 mb-8">
            Nearly three-quarters of Warrington&apos;s carriageways are unclassified — surveyed differently from A, B and C roads
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">CVI surveys — half the network each year</h3>
              <p className="text-surface-600 text-sm mb-3">
                Warrington undertakes SCANNER laser surveys on 100% of its A, B and C class carriageways annually. For
                U-roads, it uses coarse visual inspection (CVI) on approximately 50% of the network each year, achieving
                full coverage every two years.
              </p>
              <p className="text-surface-600 text-sm">
                CVI is a rapid, coarse survey — designed to cover large parts of a network quickly. At the 2022 peak,
                22% RED on 672km implies roughly <strong>148km of residential and estate roads</strong> needing maintenance
                consideration — yet any given U-road may only be condition-assessed every other year.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 change from 2026/27</h3>
              <p className="text-amber-800 text-sm mb-3">
                Warrington&apos;s report notes that from 2026/27 a new methodology based on BSI PAS 2161 will replace the
                current three-category RED/Amber/Green system with five condition categories. Authorities will need
                PAS 2161-accredited suppliers.
              </p>
              <p className="text-amber-800 text-sm">
                Until that transition, U-road condition relies on CVI data that the DfT itself acknowledges is less
                comparable nationally than SCANNER results — relevant when the council cites GREEN condition on
                aggregate scorecards.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Warrington must show it had a reasonable system for knowing the condition
              of its roads. For the 672km U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI-surveyed — and was it in a year when that section was covered?</li>
              <li>• If 22% of U-roads were RED at the 2022 peak, what was done about defects on your estate?</li>
              <li>• Does a coarse visual survey every two years meet the standard for roads the council admits are ageing together?</li>
              <li>• Was your defect on a Resilient Highway Network diversion route subject to accelerated wear from motorway traffic?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a 672km residential network it assesses with coarse visual
              inspection on a rolling two-year cycle — especially where it publicly acknowledges simultaneous end-of-life
              deterioration across New Town-era estate roads.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            18,988 pothole fills in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Warrington&apos;s published transparency data — 2024/25 was a five-year high
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,039</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,309</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,345</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,922</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,373</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,988</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">£686,806 for reactive pothole work in 2025/26</h3>
              <p className="text-surface-600 text-sm">
                Warrington allocated 11.1% of its £7.18m Highways Maintenance Block specifically to pothole reactive
                maintenance in 2025/26 — alongside £3.17m (51.3%) for structural maintenance. The council&apos;s Tarmac term
                maintenance contract was recently reappointed for six years with optional extensions, meaning reactive
                repair capacity is embedded in the delivery model.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The council&apos;s repair threshold</h3>
              <p className="text-amber-800 text-sm">
                Warrington&apos;s public guidance states potholes must be at least 4cm deep on roads (2.5cm on footways) and
                wider than 30cm to be actioned. Defects below that threshold are not normally repaired — yet can still
                damage vehicles. If your pothole was smaller than the council&apos;s intervention standard, that does not
                automatically defeat a claim if the defect was nevertheless dangerous in the specific circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* New Town admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The New Town ageing admission
          </h2>
          <p className="text-surface-600 mb-8">
            Warrington&apos;s own explanation for U-road deterioration — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="A significant challenge with our carriageway network involves significant sections of our roads built around the same time as part of the New Town development. This inevitably means that the rates of deterioration are largely the same, and most of these roads are approaching the end of their original design life and require treatment at the same time."
              source="Warrington Borough Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard
              quote="Recent studies identified that one of our largest challenges is the condition of the U Class Road network. Significant sections of our estate roads were built around the same time as New Town development. This inevitably means the rates of deterioration are largely the same, with most of these pavement sections approaching Red status (at the end of their original design life at the same time) and require resurfacing soon."
              source="Warrington Borough Council — £9M Highway Investment Programme section, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">£49m of council-funded investment</h3>
              <p className="text-surface-600 text-sm mb-3">
                Warrington invested £40m of internal borrowing from 2015–2022, delivering 612 schemes including 257
                unclassified road treatments. A further £9m was approved in July 2022, targeting U-roads (40% / £3.6m)
                and footways (60% / £5.4m) — including 69 U-road schemes treating approximately 10.2km.
              </p>
              <p className="text-surface-600 text-sm">
                That is roughly 1.5% of the 672km U-road network treated under the latest programme — while the council
                simultaneously acknowledges most New Town-era estate roads need resurfacing soon.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Motorway diversion pressure</h3>
              <p className="text-amber-800 text-sm mb-3">
                Warrington sits between the M6, M56 and M62. The council states that during motorway incidents, closures
                or severe weather, high volumes of diverted traffic adversely affect the local highway network — testing
                its Resilient Highway Network.
              </p>
              <p className="text-amber-800 text-sm">
                Combined with growing population and the council&apos;s note that revenue funding for maintenance has reduced,
                this creates documented pressure on roads that were never designed for current traffic volumes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending history */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five years of maintenance spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue spend from Warrington&apos;s transparency report — network replacement value approximately £2.3 billion
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">7,184</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">10,684</td>
                  <td className="px-4 py-3 text-center">3,071</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">4,496</td>
                  <td className="px-4 py-3 text-center">8,193</td>
                  <td className="px-4 py-3 text-center">2,817</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">5,205</td>
                  <td className="px-4 py-3 text-center">5,006</td>
                  <td className="px-4 py-3 text-center">3,318</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">3,989</td>
                  <td className="px-4 py-3 text-center">5,285</td>
                  <td className="px-4 py-3 text-center">2,999</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">3,989</td>
                  <td className="px-4 py-3 text-center">7,569</td>
                  <td className="px-4 py-3 text-center">2,072</td>
                  <td className="px-4 py-3 text-center">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">8,352</td>
                  <td className="px-4 py-3 text-center">7,824</td>
                  <td className="px-4 py-3 text-center">2,116</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Steady state on paper — pressure on the ground</h3>
            <p className="text-brand-200 mb-4">
              Warrington&apos;s stated carriageway strategy is to maintain &ldquo;steady state&rdquo; — hold current condition without
              further deterioration. The council uses Causeway HORIZONS pavement management software and batches projects
              by ward for delivery efficiency.
            </p>
            <p className="text-brand-200">
              Yet the same report acknowledges reduced revenue funding, a £2.3 billion network with 49,220 gullies and
              256 road bridges, and customer survey data showing <strong className="text-white">road condition</strong> as
              the top priority for Warrington residents. GREEN scorecards on classified-road SCANNER data do not erase the
              22% U-road RED peak or 4,373 pothole fills in a single year.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN condition, AMBER overall council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Warrington is better resourced than many authorities — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend consistently exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — A and B/C roads at 1% RED or below
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 75–85% of spend classed as preventative across five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £49m of council-funded investment programmes since 2015
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management via Causeway HORIZONS and ISO-aligned processes
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on A and B/C roads. Generic claims on main routes will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — the reason overall rating is not GREEN
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 672km U-roads (73% of network) on coarse CVI surveys every two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 22% U-road RED at 2022 survey peak — roughly 148km of estate roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 18,988 pothole fills in five years — defects forming between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits New Town-era roads reaching end of design life together
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Motorway diversion traffic acknowledged as damaging local roads
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Report the pothole first — Warrington requires a report reference number for compensation claims</li>
              <li>• Prior reports of the same defect (FixMyStreet, council records) — proof of actual notice</li>
              <li>• Photos showing size, depth and visible age — especially important given the 4cm repair threshold</li>
              <li>• Road class — on a U-road, the CVI two-year survey cycle and New Town ageing admission are your strongest arguments</li>
              <li>• Location relative to known motorway diversion routes on the Resilient Highway Network</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Warrington&apos;s own transparency data — including the GREEN/AMBER scorecard tension — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Warrington Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Warrington requires you to report the defect before claiming compensation — and you will need the reference
            number from your report. Only potholes at least 4cm deep on roads (2.5cm on footways) and wider than 30cm are
            normally actioned. Report anyway and tell us when you did so we can reference it in your pack.
          </p>
          <a
            href="https://www.warrington.gov.uk/potholes-or-damage-roads-pavements-or-footpaths"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report pothole damage — warrington.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Warrington?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            GREEN scorecards demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No New Town ageing citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 22% U-road RED peak documented</li>
                <li>✅ 672km CVI survey-cycle argued</li>
                <li>✅ 18,988 pothole fills in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Warrington</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=warrington"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Warrington claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Warrington has GREEN condition and spend scorecards — why is the overall rating AMBER?
              </h3>
              <p className="text-surface-600 text-sm">
                The Department for Transport rates Warrington GREEN on both road condition and maintenance spend, but AMBER
                on best practice — which pulls the overall rating to AMBER. For your claim, condition and whether the
                specific defect was reasonably inspected matter more than the aggregate spend scorecard. Warrington&apos;s own
                data still shows U-roads hitting 22% RED in 2022 and 4,373 pothole fills in 2024/25.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in Warrington?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 672km — roughly 73% of Warrington&apos;s 924km carriageway network. The council surveys
                approximately 50% of U-roads each year using coarse visual inspection, achieving full coverage every two
                years. At the 2022 survey, 22% of U-roads were in RED condition. The council itself notes many estate
                roads were built together during New Town development and are approaching the end of their design life at
                the same time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Warrington spending 49% above its DfT allocation weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — it strengthens the council&apos;s Section 58 defence on aggregate spend. But Section 58 turns
                on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Warrington
                projected £10.684m capital spend against a £7.184m DfT allocation in 2025/26, yet still filled 4,373
                potholes in 2024/25. High reactive volume suggests defects continue forming between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the New Town estate roads admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Warrington publicly states that significant sections of its carriageway network were built around
                the same time as part of New Town development, meaning deterioration rates are largely the same and most
                roads are approaching the end of their original design life together. That is documented knowledge of
                elevated risk across large parts of the 672km U-road network.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole fills rose to 4,373 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. 2024/25 was the highest pothole-fill year in Warrington&apos;s five-year published record. The council
                allocated £686,806 — 11.1% of its 2025/26 Highways Maintenance Block — specifically to reactive pothole
                maintenance. Filling individual defects is reactive work; the underlying U-road condition data and the
                council&apos;s own New Town ageing admission show structural pressure remains.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does motorway diversion traffic affect pothole claims in Warrington?
              </h3>
              <p className="text-surface-600 text-sm">
                It can strengthen context arguments. Warrington sits between the M6, M56 and M62, and the council states
                that during motorway incidents or closures, high volumes of diverted traffic adversely affect the local
                highway network. If your defect was on a known diversion route, the council&apos;s own Resilient Highway Network
                documentation acknowledges accelerated wear.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming against Warrington?
              </h3>
              <p className="text-surface-600 text-sm">
                Warrington&apos;s claims guidance states you should report the defect before submitting a compensation claim and
                will need the reference number from your report. The council only action potholes at least 4cm deep on
                roads (2.5cm on footways) and wider than 30cm. Reporting creates a record of notice — valuable evidence
                even if the council&apos;s repair thresholds mean smaller defects are not fixed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.warrington.gov.uk/local-highway-maintenance-transparency-report-202526"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Warrington Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
